import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { MessageDescriptor } from 'react-intl';

import { selectSuppliesProducts } from '../../../../store/supplies/supplies.selectors';
import { messages } from './supplies-card-container-messages';
import type { SuppliesDisplayLocation } from './supplies-page-constants';
import { SUPPLIES_DISPLAY_LOCATION } from './supplies-page-constants';
import { getPrioritizedSuppliesProducts } from './supplies-prioritization.service';

type SuppliesCard = {
  imageUrl: string;
  imageAlt: string;
  itemName: string;
  price: number;
  compareAtPrice?: number;
  productId: string;
  variantId: string;
};

type SuppliesConfig = {
  title: string;
  subtextMessage: MessageDescriptor;
  cards: SuppliesCard[];
};

const PAGE_CONFIG: Record<SuppliesDisplayLocation, { titleMessage: MessageDescriptor; subtextMessage: MessageDescriptor }> = {
  [SUPPLIES_DISPLAY_LOCATION.HOME]: {
    titleMessage: messages.homeTitle,
    subtextMessage: messages.homeSubtext,
  },
  [SUPPLIES_DISPLAY_LOCATION.SETTINGS]: {
    titleMessage: messages.settingsTitle,
    subtextMessage: messages.settingsSubtext,
  },
  [SUPPLIES_DISPLAY_LOCATION.SETTINGS_PACKAGES]: {
    titleMessage: messages.settingsPackagesTitle,
    subtextMessage: messages.settingsPackagesSubtext,
  },
  [SUPPLIES_DISPLAY_LOCATION.SETTINGS_PRINTING_CONNECT]: {
    titleMessage: messages.settingsPrintingConnectTitle,
    subtextMessage: messages.settingsPrintingConnectSubtext,
  },
  [SUPPLIES_DISPLAY_LOCATION.SETTINGS_PRINTING_SETUP]: {
    titleMessage: messages.settingsPrintingSetupTitle,
    subtextMessage: messages.settingsPrintingSetupSubtext,
  },
  [SUPPLIES_DISPLAY_LOCATION.SUPPLIES_PAGE]: {
    titleMessage: messages.suppliesPageTitle,
    subtextMessage: messages.suppliesPageSubtext,
  },
};

export const useSuppliesConfig = (page: SuppliesDisplayLocation): SuppliesConfig => {
  const intl = useIntl();
  const suppliesProducts = useSelector(selectSuppliesProducts);

  const prioritizedProducts = useMemo(() => getPrioritizedSuppliesProducts(page, suppliesProducts), [page, suppliesProducts]);

  const cards: SuppliesCard[] = useMemo(
    () =>
      prioritizedProducts.map((product) => ({
        imageUrl: product.secondaryImageUrl ? product.secondaryImageUrl : product.featuredImageUrl,
        imageAlt: product.secondaryImageAlt ? product.secondaryImageAlt : product.featuredImageAlt,
        itemName: product.title,
        price: parseFloat(product.price),
        compareAtPrice: product.compareAtPrice ? parseFloat(product.compareAtPrice) : undefined,
        productId: product.id,
        variantId: product.variantId,
      })),
    [prioritizedProducts],
  );

  const config = PAGE_CONFIG[page] ?? PAGE_CONFIG[SUPPLIES_DISPLAY_LOCATION.HOME];

  const title = useMemo(() => intl.formatMessage(config.titleMessage), [config.titleMessage, intl]);

  return {
    title,
    subtextMessage: config.subtextMessage,
    cards,
  };
};
